import { FC, ReactElement, useContext, useEffect, useState } from 'react'
import { getQuery } from './http'
import logger from './logger'
import React from 'react'

const log = logger.create('FEATURE_TOGGLES')

export type Features = Record<string, boolean>

/**
 * A DEV environment runs the production build, so only its address tells it
 * apart: `trainings-dev.dyn.beelday.com`, `trainings-dev2.dyn.beelday.com`.
 */
export const isDevEnvironment = (hostname: string): boolean =>
	/^[^.]+-dev\d*\./.test(hostname)

/**
 * Every feature is on, whatever `/features/production.json` says, outside a
 * production build and on a DEV environment: what is built is tried there
 * before it is released.
 */
const allFeaturesOn =
	'production' !== process.env.NODE_ENV ||
	(typeof window !== 'undefined' && isDevEnvironment(window.location.hostname))

let featuresCached: Features

if (allFeaturesOn) {
	try {
		log.warn('DEV mode - all features are enabled')
		featuresCached = new Proxy({}, { get: () => true })
	} catch (e) {
		log.error('Failed to enable all features in DEV mode', e)
	}
}
const fetchFeatures = (): Promise<Features> => {
	if (featuresCached) {
		return Promise.resolve(featuresCached)
	}

	try {
		const featuresId = getQuery('features') || 'production'
		return fetch(`/features/${featuresId}.json`, { cache: 'no-store' })
			.then(res => res.json())
			.then(features => {
				featuresCached = features
				return features
			})
			.catch(() => ({}))
	} catch (e) {
		return Promise.resolve({})
	}
}

const featuresContext = React.createContext<Features | undefined>({})

export const FeaturesLoader: FC = ({ children }) => {
	const [features, setFeatures] = useState<Features>()

	useEffect(() => {
		fetchFeatures().then(setFeatures)
	}, [])

	return (
		<featuresContext.Provider value={features}>
			{features && children}
		</featuresContext.Provider>
	)
}

type Props = {
	name: string
	children: () => ReactElement | null
}

export const FeatureToggle: FC<Props> = ({ children, name }) => {
	const features = useContext(featuresContext)

	if (features && features[name]) {
		return children()
	} else {
		return null
	}
}

export const useFeatureEnabled = (name: string): boolean => {
	const features = useContext(featuresContext)

	return !!features && features[name]
}
